import { useState, useRef, useEffect } from 'react'
import { Plus, Users, Trash2, Upload, Search, X, Pencil, UserPlus, ChevronDown, FileSpreadsheet, ArrowRight, CheckCircle2, AlertCircle, Loader2, Database } from 'lucide-react'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import {
  useEmLists, useCreateEmList, useUpdateEmList, useDeleteEmList,
  useEmContacts, useCreateEmContact, useDeleteEmContact, useUpdateEmContact,
  useUploadCsv, useBulkDeleteEmContacts, useBulkUpdateCurva, useConvertToLead,
  useSearchableCrmContacts, useImportContactsFromCrm, useSearchableCrmFacets,
  type EmContactList, type EmContact, type CrmContactWithLead, type CrmContactFilters,
} from '@/hooks/useEmailMarketing'
import api from '@/lib/api'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

const SYSTEM_FIELDS = [
  { value: '', label: '\u2014 Ignorar \u2014' },
  { value: 'first_name', label: 'Nome' },
  { value: 'last_name', label: 'Sobrenome' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'company_cnpj', label: 'CNPJ da Empresa' },
  { value: 'company_name', label: 'Nome da Empresa' },
  { value: 'curva_abc', label: 'Curva ABC' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'segmento', label: 'Segmento' },
]

function autoDetectField(colName: string): string {
  const n = colName.toLowerCase().trim()
  if (/^(nome|first.?name|name)$/i.test(n)) return 'first_name'
  if (/^(sobrenome|last.?name|surname)$/i.test(n)) return 'last_name'
  if (/^(e.?mail|email)$/i.test(n)) return 'email'
  if (/^(whatsapp|wpp|celular|mobile|telefone|phone)$/i.test(n)) return 'whatsapp'
  if (/^(cnpj.?empresa|company.?cnpj)$/i.test(n)) return 'company_cnpj'
  if (/^(empresa|company|raz.o.?social|company.?name)$/i.test(n)) return 'company_name'
  if (/^(curva|curva.?abc|abc)$/i.test(n)) return 'curva_abc'
  if (/^(cnpj)$/i.test(n)) return 'cnpj'
  if (/^(segmento|segment|setor|industry)$/i.test(n)) return 'segmento'
  return ''
}

function CurvaBadge({ value }: { value?: string }) {
  if (!value) return <span className="text-muted-foreground">\u2014</span>
  const colors: Record<string, string> = { A: 'bg-green-500/20 text-green-400', B: 'bg-yellow-500/20 text-yellow-400', C: 'bg-red-500/20 text-red-400' }
  return <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${colors[value] || 'bg-muted text-muted-foreground'}`}>{value}</span>
}

export default function EmListsPage() {
  const lists = useEmLists()
  const createMut = useCreateEmList()
  const updateListMut = useUpdateEmList()
  const deleteMut = useDeleteEmList()
  const [editingListId, setEditingListId] = useState<string | null>(null)
  const [listNameDraft, setListNameDraft] = useState('')

  const startEditList = (id: string, currentName: string) => {
    setEditingListId(id)
    setListNameDraft(currentName)
  }
  const saveListName = async (id: string) => {
    const name = listNameDraft.trim()
    if (!name) { setEditingListId(null); return }
    try {
      await updateListMut.mutateAsync({ id, name })
      setEditingListId(null)
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Erro ao renomear lista')
    }
  }
  const createContactMut = useCreateEmContact()
  const deleteContactMut = useDeleteEmContact()
  const updateContactMut = useUpdateEmContact()
  const bulkDeleteMut = useBulkDeleteEmContacts()
  const bulkCurvaMut = useBulkUpdateCurva()
  const convertLeadMut = useConvertToLead()
  const uploadCsvMut = useUploadCsv()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // CRM Import modal state
  const [showCrmImport, setShowCrmImport] = useState(false)
  const [crmFilters, setCrmFilters] = useState<CrmContactFilters>({ per_page: 100, page: 1, only_with_email: true })
  const [crmSelected, setCrmSelected] = useState<Set<string>>(new Set())
  const crmContacts = useSearchableCrmContacts(crmFilters, showCrmImport)
  const crmFacets = useSearchableCrmFacets(showCrmImport)
  const importFromCrmMut = useImportContactsFromCrm()

  const [selectedList, setSelectedList] = useState<EmContactList | null>(null)
  const [confirmDeleteContact, setConfirmDeleteContact] = useState<EmContact | null>(null)
  const [confirmDeleteList, setConfirmDeleteList] = useState<EmContactList | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [showAddContact, setShowAddContact] = useState(false)
  const [editingContact, setEditingContact] = useState<EmContact | null>(null)
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', whatsapp: '', company_cnpj: '', company_name: '', curva_abc: '', cnpj: '', segmento: '' })

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Bulk action menu
  const [showBulkMenu, setShowBulkMenu] = useState(false)

  // CSV
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvData, setCsvData] = useState<{ columns: string[]; sample_rows: Record<string, string>[]; total_rows: number } | null>(null)
  const [csvMappings, setCsvMappings] = useState<Record<string, string>>({})
  const [showCsvModal, setShowCsvModal] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importProgress, setImportProgress] = useState(0)
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null)

  // Paginação server-side dos contatos da lista (listas grandes — 9k+ contatos)
  const CONTACTS_PER_PAGE = 50
  const [contactsPage, setContactsPage] = useState(1)
  // Reseta a página ao trocar de lista
  useEffect(() => { setContactsPage(1) }, [selectedList?.id])
  const contacts = useEmContacts(selectedList?.id || '', { page: contactsPage, per_page: CONTACTS_PER_PAGE })
  const contactsItems = contacts.data?.items || []
  const contactsTotal = contacts.data?.total || 0
  const contactsTotalPages = contacts.data?.total_pages || 1

  // DataGrid columns
  const columns: DataGridColumn<EmContact>[] = [
    {
      id: 'select', header: '', sortable: false, filterable: false, minWidth: '40px',
      accessor: (r) => (
        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} onClick={e => e.stopPropagation()} className="rounded" />
      ),
    },
    { id: 'first_name', header: 'Nome', accessor: (r) => `${r.first_name} ${r.last_name || ''}`.trim(), sortValue: (r) => r.first_name, sortable: true, filterable: true },
    { id: 'email', header: 'Email', accessor: (r) => r.email, sortValue: (r) => r.email, sortable: true, filterable: true },
    { id: 'whatsapp', header: 'WhatsApp', accessor: (r) => r.whatsapp || '\u2014', sortValue: (r) => r.whatsapp || '', sortable: true, filterable: true, minWidth: '120px' },
    { id: 'company_name', header: 'Empresa', accessor: (r) => r.company_name || '\u2014', sortValue: (r) => r.company_name || '', sortable: true, filterable: true },
    { id: 'curva_abc', header: 'Curva', accessor: (r) => <CurvaBadge value={(r as any).curva_abc} />, filterValue: (r) => (r as any).curva_abc || '', sortValue: (r) => (r as any).curva_abc || '', sortable: true, filterable: true, minWidth: '70px' },
    { id: 'segmento', header: 'Segmento', accessor: (r) => (r as any).segmento || '\u2014', sortValue: (r) => (r as any).segmento || '', sortable: true, filterable: true },
    { id: 'cnpj', header: 'CNPJ', accessor: (r) => (r as any).cnpj || '\u2014', sortValue: (r) => (r as any).cnpj || '', sortable: true, filterable: true },
    {
      id: 'actions', header: 'A\u00e7\u00f5es', sortable: false, filterable: false, minWidth: '140px',
      accessor: (r) => (
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          <button onClick={() => openEditContact(r)} className="p-1 rounded hover:bg-accent" title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={() => { if (confirm('Converter em lead?')) convertLeadMut.mutateAsync({ listId: selectedList!.id, contactId: r.id }).then(res => alert(`Lead criado! ID: ${(res as any).lead_id}`)) }} className="p-1 rounded hover:bg-accent" title="Converter em Lead"><UserPlus className="w-3.5 h-3.5 text-green-400" /></button>
          <button onClick={() => setConfirmDeleteContact(r)} className="p-1 rounded hover:bg-accent" title="Excluir"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
        </div>
      ),
    },
  ]

  const handleCreateList = async () => {
    await createMut.mutateAsync({ name: newName, description: newDesc })
    setShowNew(false); setNewName(''); setNewDesc('')
  }

  const openAddContact = () => {
    setEditingContact(null)
    setContactForm({ first_name: '', last_name: '', email: '', whatsapp: '', company_cnpj: '', company_name: '', curva_abc: '', cnpj: '', segmento: '' })
    setShowAddContact(true)
  }

  const openEditContact = (c: EmContact) => {
    setEditingContact(c)
    setContactForm({
      first_name: c.first_name, last_name: c.last_name || '', email: c.email,
      whatsapp: c.whatsapp || '', company_cnpj: c.company_cnpj || '', company_name: c.company_name || '',
      curva_abc: (c as any).curva_abc || '', cnpj: (c as any).cnpj || '', segmento: (c as any).segmento || '',
    })
    setShowAddContact(true)
  }

  const handleSaveContact = async () => {
    if (!selectedList) return
    if (editingContact) {
      await updateContactMut.mutateAsync({ listId: selectedList.id, contactId: editingContact.id, ...contactForm })
    } else {
      await createContactMut.mutateAsync({ listId: selectedList.id, ...contactForm })
    }
    setShowAddContact(false)
    setEditingContact(null)
  }

  // Bulk actions
  const handleBulkDelete = async () => {
    if (!selectedList || selected.size === 0) return
    if (!confirm(`Excluir ${selected.size} contato(s)?`)) return
    await bulkDeleteMut.mutateAsync({ listId: selectedList.id, contactIds: Array.from(selected) })
    setSelected(new Set())
    setShowBulkMenu(false)
  }

  const handleBulkCurva = async (curva: string) => {
    if (!selectedList || selected.size === 0) return
    await bulkCurvaMut.mutateAsync({ listId: selectedList.id, contactIds: Array.from(selected), curva })
    setSelected(new Set())
    setShowBulkMenu(false)
  }

  // CSV
  const handleCsvFileSelect = async (file: File) => {
    if (!selectedList) return
    setCsvFile(file)
    setImportResult(null)
    try {
      const result = await uploadCsvMut.mutateAsync({ listId: selectedList.id, file })
      setCsvData(result)
      const autoMap: Record<string, string> = {}
      result.columns.forEach(col => { autoMap[col] = autoDetectField(col) })
      setCsvMappings(autoMap)
      setShowCsvModal(true)
    } catch (e: any) {
      alert('Erro ao ler arquivo: ' + (e?.message || 'Formato inv\u00e1lido'))
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleCsvImport = async () => {
    if (!selectedList || !csvFile || !csvData) return
    setImporting(true); setImportProgress(10)
    try {
      const mappings = Object.entries(csvMappings).filter(([_, sys]) => sys).map(([csv_column, system_field]) => ({ csv_column, system_field }))
      setImportProgress(30)
      const formData = new FormData(); formData.append('file', csvFile)
      const { data } = await api.post(`/v1/email-marketing/lists/${selectedList.id}/import/confirm`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, params: { mappings: JSON.stringify({ mappings }) } })
      setImportProgress(90); setImportResult(data); setImportProgress(100)
      lists.refetch(); contacts.refetch()
    } catch (e: any) {
      setImportResult({ imported: 0, skipped: 0, errors: [e?.response?.data?.detail || e?.message || 'Erro'] })
      setImportProgress(100)
    } finally { setImporting(false) }
  }

  const closeCsvModal = () => { setShowCsvModal(false); setCsvFile(null); setCsvData(null); setCsvMappings({}); setImportResult(null); setImportProgress(0) }

  // Toggle selection
  const toggleSelect = (id: string) => {
    const next = new Set(selected)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelected(next)
  }
  const toggleAll = () => {
    const items = contactsItems
    if (selected.size === items.length) setSelected(new Set())
    else setSelected(new Set(items.map(c => c.id)))
  }

  return (
    <div className="p-6 flex gap-4 h-[calc(100vh-64px)]">
      {/* Left: Lists */}
      <div className="w-72 flex-shrink-0 flex flex-col bg-card border rounded-lg overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between">
          <h2 className="font-semibold text-sm">Listas</h2>
          <button onClick={() => setShowNew(true)} className="p-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90"><Plus className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {(lists.data || []).map(l => {
            const isEditing = editingListId === l.id
            return (
              <div key={l.id} className={`group p-3 border-b transition-colors ${selectedList?.id === l.id ? 'bg-accent/20 border-l-2 border-l-primary' : 'hover:bg-accent/30'}`}>
                <div className="flex items-center justify-between gap-2">
                  {isEditing ? (
                    <div className="flex-1 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        autoFocus
                        type="text"
                        value={listNameDraft}
                        onChange={(e) => setListNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); saveListName(l.id) }
                          if (e.key === 'Escape') { e.preventDefault(); setEditingListId(null) }
                        }}
                        onBlur={() => saveListName(l.id)}
                        className="flex-1 min-w-0 h-7 px-2 text-sm rounded border border-input bg-background"
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setSelectedList(l); setSelected(new Set()) }}
                      className="flex-1 min-w-0 text-left"
                    >
                      <span className="font-medium text-sm truncate block">{l.name}</span>
                    </button>
                  )}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!isEditing && (
                      <button
                        onClick={(e) => { e.stopPropagation(); startEditList(l.id, l.name) }}
                        className="p-1 rounded hover:bg-accent opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Renomear lista"
                      >
                        <Pencil className="w-3 h-3 text-muted-foreground" />
                      </button>
                    )}
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" />{l.contact_count}</span>
                  </div>
                </div>
                {l.description && !isEditing && <p className="text-xs text-muted-foreground mt-0.5 truncate">{l.description}</p>}
              </div>
            )
          })}
          {(lists.data || []).length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">Nenhuma lista</div>}
        </div>
      </div>

      {/* Right: Contacts */}
      <div className="flex-1 flex flex-col bg-card border rounded-lg overflow-hidden">
        {selectedList ? (
          <>
            {/* Toolbar */}
            <div className="p-3 border-b flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="font-semibold">{selectedList.name}</h2>
                <span className="text-xs text-muted-foreground">{selectedList.contact_count} contatos</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Bulk actions */}
                {selected.size > 0 && (
                  <div className="relative">
                    <button onClick={() => setShowBulkMenu(!showBulkMenu)} className="px-3 py-1.5 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-lg flex items-center gap-1">
                      {selected.size} selecionado(s) <ChevronDown className="w-3 h-3" />
                    </button>
                    {showBulkMenu && (
                      <div className="absolute right-0 top-full mt-1 bg-card border rounded-lg shadow-xl z-20 py-1 w-48">
                        <button onClick={handleBulkDelete} className="w-full text-left px-3 py-2 text-sm hover:bg-accent/30 flex items-center gap-2 text-red-400"><Trash2 className="w-3.5 h-3.5" /> Excluir selecionados</button>
                        <div className="border-t my-1" />
                        <p className="px-3 py-1 text-[10px] text-muted-foreground uppercase">Alterar Curva ABC</p>
                        <button onClick={() => handleBulkCurva('A')} className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent/30"><CurvaBadge value="A" /> Curva A</button>
                        <button onClick={() => handleBulkCurva('B')} className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent/30"><CurvaBadge value="B" /> Curva B</button>
                        <button onClick={() => handleBulkCurva('C')} className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent/30"><CurvaBadge value="C" /> Curva C</button>
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => { setCrmSelected(new Set()); setCrmFilters({ per_page: 100, page: 1, only_with_email: true }); setShowCrmImport(true) }} className="px-3 py-1.5 text-xs border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 flex items-center gap-1">
                  <Database className="w-3.5 h-3.5" /> Importar do CRM
                </button>
                <label className="px-3 py-1.5 text-xs border rounded-lg hover:bg-accent/50 cursor-pointer flex items-center gap-1">
                  <Upload className="w-3.5 h-3.5" /> CSV
                  <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => { if (e.target.files?.[0]) handleCsvFileSelect(e.target.files[0]) }} />
                </label>
                <button onClick={openAddContact} className="px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-lg flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" /> Contato
                </button>
                <button onClick={() => setConfirmDeleteList(selectedList)} className="p-1.5 rounded-lg hover:bg-accent" title="Excluir lista"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>

            {/* DataGrid — overflow-y-auto deixa rolar dentro do painel quando a lista
                excede a altura da viewport (fix 2026-05-15: antes era overflow-hidden
                e cortava as últimas linhas em zoom 100%). */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <DataGrid<EmContact>
                  gridId="em-contacts"
                  columns={columns}
                  data={contactsItems}
                  isLoading={contacts.isLoading}
                  emptyMessage="Nenhum contato nesta lista"
                  emptyIcon={<Users className="w-8 h-8 text-muted-foreground" />}
                  pageSize={CONTACTS_PER_PAGE}
                  selectable
                  selectedIds={selected}
                  onSelectionChange={setSelected}
                />
              </div>

              {/* Paginação server-side */}
              {contactsTotal > 0 && (
                <div className="border-t px-3 py-2 flex items-center justify-between text-xs text-muted-foreground bg-card flex-shrink-0">
                  <div>
                    {contactsTotal} contato{contactsTotal === 1 ? '' : 's'} no total
                    {contactsTotalPages > 1 && <> · página {contactsPage} de {contactsTotalPages}</>}
                  </div>
                  {contactsTotalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <button
                        disabled={contactsPage <= 1 || contacts.isFetching}
                        onClick={() => setContactsPage(1)}
                        className="px-2 py-1 border rounded hover:bg-accent/50 disabled:opacity-30"
                        title="Primeira"
                      >«</button>
                      <button
                        disabled={contactsPage <= 1 || contacts.isFetching}
                        onClick={() => setContactsPage(contactsPage - 1)}
                        className="px-2 py-1 border rounded hover:bg-accent/50 disabled:opacity-30"
                      >Anterior</button>
                      <button
                        disabled={contactsPage >= contactsTotalPages || contacts.isFetching}
                        onClick={() => setContactsPage(contactsPage + 1)}
                        className="px-2 py-1 border rounded hover:bg-accent/50 disabled:opacity-30"
                      >Próxima</button>
                      <button
                        disabled={contactsPage >= contactsTotalPages || contacts.isFetching}
                        onClick={() => setContactsPage(contactsTotalPages)}
                        className="px-2 py-1 border rounded hover:bg-accent/50 disabled:opacity-30"
                        title="Última"
                      >»</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>Selecione uma lista</p>
            </div>
          </div>
        )}
      </div>

      {/* CRM Import Modal */}
      {showCrmImport && selectedList && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowCrmImport(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative w-full max-w-6xl max-h-[90vh] bg-card border rounded-xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-emerald-400" />
                <div>
                  <h3 className="font-semibold">Importar Contatos do CRM</h3>
                  <p className="text-xs text-muted-foreground">Selecione os contatos que deseja adicionar à lista <span className="text-foreground">{selectedList.name}</span></p>
                </div>
              </div>
              <button onClick={() => setShowCrmImport(false)}><X className="w-5 h-5" /></button>
            </div>

            {/* Filters */}
            <div className="p-4 border-b grid grid-cols-1 md:grid-cols-5 gap-2 flex-shrink-0">
              <div className="relative md:col-span-2">
                <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
                <input type="text" placeholder="Buscar nome, email, empresa..." value={crmFilters.search || ''} onChange={e => setCrmFilters({ ...crmFilters, search: e.target.value, page: 1 })} className="w-full pl-8 pr-2 py-2 text-sm border rounded-lg bg-background" />
              </div>
              <input type="text" placeholder="Nome do Lead" value={crmFilters.lead_title || ''} onChange={e => setCrmFilters({ ...crmFilters, lead_title: e.target.value, page: 1 })} className="px-2 py-2 text-sm border rounded-lg bg-background" />
              <input type="text" placeholder="Nome da Empresa" value={crmFilters.company_name || ''} onChange={e => setCrmFilters({ ...crmFilters, company_name: e.target.value, page: 1 })} className="px-2 py-2 text-sm border rounded-lg bg-background" />
              <select value={crmFilters.origin || ''} onChange={e => setCrmFilters({ ...crmFilters, origin: e.target.value, page: 1 })} className="px-2 py-2 text-sm border rounded-lg bg-background">
                <option value="">Todas as Origens</option>
                <option value="event">Evento</option>
                <option value="bdr_partners">BDR/Parceiro/Indicação</option>
                <option value="active_prospecting">Prospecção Ativa</option>
                <option value="website">Website</option>
              </select>
              <select value={crmFilters.sub_origin || ''} onChange={e => setCrmFilters({ ...crmFilters, sub_origin: e.target.value, page: 1 })} className="px-2 py-2 text-sm border rounded-lg bg-background">
                <option value="">Todas as Sub-Origens</option>
                {(crmFacets.data?.sub_origins || []).map(so => <option key={so} value={so}>{so}</option>)}
                {crmFacets.data?.has_null_sub_origin && <option value="__none__">— Sem sub-origem —</option>}
              </select>
              <select value={crmFilters.owner_id || ''} onChange={e => setCrmFilters({ ...crmFilters, owner_id: e.target.value, page: 1 })} className="px-2 py-2 text-sm border rounded-lg bg-background">
                <option value="">Todos os Responsáveis</option>
                {(crmFacets.data?.owners || []).map(o => <option key={o.id} value={o.id}>{o.name} ({o.lead_count})</option>)}
                <option value="__none__">— Sem responsável —</option>
              </select>
              <label className="flex items-center gap-2 text-xs text-muted-foreground md:col-span-3">
                <input type="checkbox" checked={crmFilters.only_with_email !== false} onChange={e => setCrmFilters({ ...crmFilters, only_with_email: e.target.checked, page: 1 })} />
                Apenas contatos com e-mail
              </label>
              <button onClick={() => { setCrmFilters({ per_page: 100, page: 1, only_with_email: true }) }} className="px-3 py-2 text-xs border rounded-lg hover:bg-accent/50">Limpar filtros</button>
            </div>

            {/* Bulk select bar */}
            <div className="px-4 py-2 border-b flex items-center justify-between bg-muted/20 flex-shrink-0">
              <div className="flex items-center gap-3 text-sm">
                <button onClick={() => {
                  const items = crmContacts.data?.items || []
                  if (items.every(c => crmSelected.has(c.id))) {
                    const next = new Set(crmSelected); items.forEach(c => next.delete(c.id)); setCrmSelected(next)
                  } else {
                    const next = new Set(crmSelected); items.forEach(c => next.add(c.id)); setCrmSelected(next)
                  }
                }} className="px-2 py-1 text-xs border rounded hover:bg-accent/50">Selecionar todos da página</button>
                <span className="text-xs text-muted-foreground">{crmSelected.size} selecionado(s) · {crmContacts.data?.total || 0} resultado(s)</span>
              </div>
              <button
                disabled={crmSelected.size === 0 || importFromCrmMut.isPending}
                onClick={async () => {
                  if (!selectedList) return
                  const result = await importFromCrmMut.mutateAsync({ listId: selectedList.id, contactIds: Array.from(crmSelected) })
                  alert(`Importação concluída:\n• Solicitados: ${result.requested}\n• Inseridos: ${result.inserted}\n• Sem e-mail: ${result.skipped_no_email}\n• Já na lista: ${result.skipped_duplicate}\n• Não encontrados: ${result.skipped_not_found}`)
                  setShowCrmImport(false)
                  setCrmSelected(new Set())
                  contacts.refetch()
                  lists.refetch()
                }}
                className="px-4 py-1.5 text-sm bg-emerald-500 text-white rounded-lg disabled:opacity-40 flex items-center gap-2"
              >
                {importFromCrmMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Adicionar {crmSelected.size > 0 ? crmSelected.size : ''} à lista
              </button>
            </div>

            {/* Results table */}
            <div className="flex-1 overflow-auto">
              {crmContacts.isLoading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando…</div>
              ) : (crmContacts.data?.items || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground"><Search className="w-10 h-10 mb-2 opacity-30" />Nenhum contato com os filtros atuais</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 sticky top-0">
                    <tr>
                      <th className="p-2 text-left w-8"></th>
                      <th className="p-2 text-left">Contato</th>
                      <th className="p-2 text-left">Cargo</th>
                      <th className="p-2 text-left">Empresa</th>
                      <th className="p-2 text-left">Lead</th>
                      <th className="p-2 text-left">Origem</th>
                      <th className="p-2 text-left">Sub-Origem</th>
                      <th className="p-2 text-left">Responsável</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(crmContacts.data?.items || []).map(c => (
                      <tr key={c.id + (c.lead_id || '')} className={`border-b hover:bg-accent/20 ${crmSelected.has(c.id) ? 'bg-emerald-500/5' : ''}`}>
                        <td className="p-2">
                          <input type="checkbox" checked={crmSelected.has(c.id)} onChange={() => {
                            const next = new Set(crmSelected)
                            next.has(c.id) ? next.delete(c.id) : next.add(c.id)
                            setCrmSelected(next)
                          }} />
                        </td>
                        <td className="p-2">
                          <div className="font-medium">{c.first_name} {c.last_name || ''}</div>
                          <div className="text-xs text-muted-foreground">{c.email || '—'}</div>
                        </td>
                        <td className="p-2 text-muted-foreground">{c.job_title || '—'}</td>
                        <td className="p-2">{c.company_name || '—'}</td>
                        <td className="p-2">{c.lead_title || <span className="text-muted-foreground">—</span>}</td>
                        <td className="p-2">{c.lead_origin === 'event' ? 'Evento' : c.lead_origin === 'bdr_partners' ? 'BDR/Parceiro/Indicação' : c.lead_origin === 'active_prospecting' ? 'Prospecção Ativa' : c.lead_origin === 'website' ? 'Website' : <span className="text-muted-foreground">—</span>}</td>
                        <td className="p-2 text-xs text-muted-foreground">{c.lead_sub_origin || '—'}</td>
                        <td className="p-2 text-xs text-muted-foreground">{c.lead_owner_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {(crmContacts.data?.pages || 1) > 1 && (
              <div className="p-3 border-t flex items-center justify-between text-sm flex-shrink-0">
                <span className="text-muted-foreground">Página {crmContacts.data?.page} de {crmContacts.data?.pages}</span>
                <div className="flex gap-2">
                  <button disabled={(crmFilters.page || 1) <= 1} onClick={() => setCrmFilters({ ...crmFilters, page: (crmFilters.page || 1) - 1 })} className="px-3 py-1 text-xs border rounded disabled:opacity-30">Anterior</button>
                  <button disabled={(crmFilters.page || 1) >= (crmContacts.data?.pages || 1)} onClick={() => setCrmFilters({ ...crmFilters, page: (crmFilters.page || 1) + 1 })} className="px-3 py-1 text-xs border rounded disabled:opacity-30">Próxima</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CSV Modal */}
      {showCsvModal && csvData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeCsvModal}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative w-full max-w-3xl max-h-[90vh] bg-card border rounded-xl shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3"><FileSpreadsheet className="w-5 h-5 text-primary" /><div><h3 className="font-semibold">Importar Contatos</h3><p className="text-xs text-muted-foreground">{csvFile?.name} \u2014 {csvData.total_rows} linhas</p></div></div>
              <button onClick={closeCsvModal}><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {!importResult ? (
                <>
                  <div className="border rounded-lg overflow-hidden mb-4">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-muted/50 text-xs uppercase text-muted-foreground"><th className="p-2.5 text-left">Coluna Planilha</th><th className="p-2.5 text-center w-10">{'\u2192'}</th><th className="p-2.5 text-left">Campo Sistema</th><th className="p-2.5 text-left hidden md:table-cell">Amostra</th></tr></thead>
                      <tbody>{csvData.columns.map(col => (<tr key={col} className="border-t"><td className="p-2.5 font-medium text-amber-400">{col}</td><td className="p-2.5 text-center text-primary font-bold">{'\u2192'}</td><td className="p-2.5"><select value={csvMappings[col] || ''} onChange={e => setCsvMappings({...csvMappings, [col]: e.target.value})} className="w-full px-2 py-1.5 text-sm bg-background border rounded-md">{SYSTEM_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}</select></td><td className="p-2.5 text-xs text-muted-foreground hidden md:table-cell truncate max-w-[200px]">{csvData.sample_rows[0]?.[col] || '\u2014'}</td></tr>))}</tbody>
                    </table>
                  </div>
                  {!Object.values(csvMappings).includes('email') && (<div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 rounded-lg p-2.5 mb-4"><AlertCircle className="w-4 h-4" /><span>Campo <strong>Email</strong> obrigat\u00f3rio.</span></div>)}
                  {importing && (<div className="mb-4"><div className="flex items-center gap-2 mb-2"><Loader2 className="w-4 h-4 animate-spin text-primary" /><span className="text-sm">Importando...</span></div><div className="h-3 bg-muted rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-primary to-[#00d4aa] rounded-full transition-all" style={{width:`${importProgress}%`}} /></div></div>)}
                </>
              ) : (
                <div className="py-8 text-center">
                  {importResult.imported > 0 ? <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" /> : <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />}
                  <h3 className="text-lg font-semibold mb-2">Conclu\u00eddo</h3>
                  <div className="flex justify-center gap-6 mb-4">
                    <div className="text-center"><div className="text-2xl font-bold text-green-500">{importResult.imported}</div><div className="text-xs text-muted-foreground">Importados</div></div>
                    <div className="text-center"><div className="text-2xl font-bold text-amber-500">{importResult.skipped}</div><div className="text-xs text-muted-foreground">Ignorados</div></div>
                  </div>
                  {importResult.errors.length > 0 && <div className="text-left bg-red-500/10 rounded-lg p-3 max-h-32 overflow-y-auto text-xs text-red-400 mx-auto max-w-md">{importResult.errors.map((err, i) => <div key={i}>{err}</div>)}</div>}
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-between flex-shrink-0">
              <span className="text-xs text-muted-foreground">{csvData.total_rows} linhas \u2022 {Object.values(csvMappings).filter(v => v).length} campos</span>
              <div className="flex gap-2">
                <button onClick={closeCsvModal} className="px-4 py-2 text-sm border rounded-lg">{importResult ? 'Fechar' : 'Cancelar'}</button>
                {!importResult && <button onClick={handleCsvImport} disabled={importing || !Object.values(csvMappings).includes('email')} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg disabled:opacity-40 flex items-center gap-2">{importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importando...</> : <><Upload className="w-4 h-4" /> Importar {csvData.total_rows}</>}</button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New List Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative w-full max-w-md bg-card border rounded-xl shadow-2xl p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">Nova Lista</h3>
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-muted-foreground">Nome</label><input value={newName} onChange={e => setNewName(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="Prospects LGPD 2026" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Descri\u00e7\u00e3o</label><input value={newDesc} onChange={e => setNewDesc(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
              <button onClick={handleCreateList} disabled={!newName} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg disabled:opacity-50">Criar</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Contact Modal */}
      {showAddContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowAddContact(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative w-full max-w-lg bg-card border rounded-xl shadow-2xl p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">{editingContact ? 'Editar Contato' : 'Adicionar Contato'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground">Nome *</label><input value={contactForm.first_name} onChange={e => setContactForm({...contactForm, first_name: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Sobrenome</label><input value={contactForm.last_name} onChange={e => setContactForm({...contactForm, last_name: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Email *</label><input value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" type="email" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">WhatsApp</label><input value={contactForm.whatsapp} onChange={e => setContactForm({...contactForm, whatsapp: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">CNPJ Empresa</label><input value={contactForm.company_cnpj} onChange={e => setContactForm({...contactForm, company_cnpj: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Empresa</label><input value={contactForm.company_name} onChange={e => setContactForm({...contactForm, company_name: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Curva ABC</label><select value={contactForm.curva_abc} onChange={e => setContactForm({...contactForm, curva_abc: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md"><option value="">Selecionar</option><option value="A">A</option><option value="B">B</option><option value="C">C</option></select></div>
              <div><label className="text-xs font-medium text-muted-foreground">CNPJ</label><input value={contactForm.cnpj} onChange={e => setContactForm({...contactForm, cnpj: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" /></div>
              <div className="col-span-2"><label className="text-xs font-medium text-muted-foreground">Segmento</label><input value={contactForm.segmento} onChange={e => setContactForm({...contactForm, segmento: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="Varejo, Ind\u00fastria..." /></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowAddContact(false)} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
              <button onClick={handleSaveContact} disabled={!contactForm.first_name || !contactForm.email} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg disabled:opacity-50">{editingContact ? 'Salvar' : 'Adicionar'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeleteContact}
        title="Excluir contato?"
        message={`O contato ${confirmDeleteContact?.email ? '"' + confirmDeleteContact.email + '"' : 'selecionado'} será removido da lista. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={deleteContactMut.isPending}
        onConfirm={async () => {
          if (!confirmDeleteContact || !selectedList) return
          await deleteContactMut.mutateAsync({ listId: selectedList.id, contactId: confirmDeleteContact.id })
          setConfirmDeleteContact(null)
        }}
        onCancel={() => setConfirmDeleteContact(null)}
      />

      <ConfirmDialog
        open={!!confirmDeleteList}
        title="Excluir lista de contatos?"
        message={`A lista "${confirmDeleteList?.name || ''}" e todos os contatos vinculados serão removidos. Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir lista"
        variant="destructive"
        isLoading={deleteMut.isPending}
        onConfirm={async () => {
          if (!confirmDeleteList) return
          await deleteMut.mutateAsync(confirmDeleteList.id)
          setSelectedList(null)
          setConfirmDeleteList(null)
        }}
        onCancel={() => setConfirmDeleteList(null)}
      />
    </div>
  )
}
